import '@/styles/globals.css'
import { ComponentType, ReactNode } from 'react'
import { createInertiaApp, http } from '@inertiajs/react'
import { createRoot } from 'react-dom/client'
import snakecaseKeys from 'snakecase-keys'
import { registerSW } from 'virtual:pwa-register'
import { Provider as RollbarProvider } from '@rollbar/react'
import { SW_UPDATE_INTERVAL_MS } from '@/shared/lib/sw-update-interval'
import AppLayout from '@/shared/layouts/AppLayout'
import { ErrorBoundary } from '@/shared/components/ErrorBoundary'
import type { SharedProps } from '@/shared/types/SharedProps'

type InertiaPageModule = {
  default: ComponentType<Record<string, unknown>> & {
    layout?: (page: ReactNode) => ReactNode
  }
}

http.onRequest((config) => {
  if (config.data !== null && config.data !== undefined && !(config.data instanceof FormData)) {
    config.data = snakecaseKeys(config.data as Record<string, unknown>, { deep: true })
  }
  return config
})

registerSW({
  onRegisteredSW(_swUrl, registration) {
    if (registration) {
      // Periodically poll for SW updates so long-lived open tabs don't wait
      // for a natural browser check (which may take hours).
      setInterval(() => {
        registration.update()
      }, SW_UPDATE_INTERVAL_MS)
    }
  },
})

createInertiaApp({
  resolve: (name) => {
    // Excludes *.test.tsx — colocated page-scoped component tests (e.g.
    // Dashboard/Tasks/HideNonComplianceToggle.test.tsx) must not be eagerly
    // evaluated here: Vitest's `describe()` throws outside the test runner,
    // which previously crashed this resolver and blanked the entire app.
    const pages = import.meta.glob(['../pages/**/*.tsx', '!../pages/**/*.test.tsx'], {
      eager: true,
    }) as Record<string, InertiaPageModule>
    const page = pages[`../pages/${name}.tsx`]
    if (!page) {
      throw new Error(`Unknown Inertia page: ${name}`)
    }
    if (!name.startsWith('Auth/')) {
      page.default.layout =
        page.default.layout ?? ((page: ReactNode) => <AppLayout>{page}</AppLayout>)
    }
    return page
  },
  setup({ el, App, props }) {
    // appInfo is shared on every Inertia response (inertia_share), so the initial
    // page's props already carry the server's Rails.env — no separate fetch needed.
    const { environment, rollbarClientAccessToken, logrocketAppId } = (
      props.initialPage.props as unknown as SharedProps
    ).appInfo
    if (!['development', 'test'].includes(environment) && logrocketAppId) {
      // Dynamic import keeps LogRocket out of the dev bundle entirely.
      import('logrocket').then(({ default: LogRocket }) => {
        LogRocket.init(logrocketAppId)
      })
    }
    const rollbarConfig = {
      accessToken: rollbarClientAccessToken,
      environment,
      captureUncaught: true,
      captureUnhandledRejections: true,
      enabled: !['development', 'test'].includes(environment),
      ignoredMessages: [/^Unknown Inertia page:/],
    }
    createRoot(el).render(
      <RollbarProvider config={rollbarConfig}>
        <ErrorBoundary>
          <App {...props} />
        </ErrorBoundary>
      </RollbarProvider>,
    )
  },
})
